import classnames from "classnames";
import {useMemo} from "react";
import LocationIcon from "../../assets/logo/LocationIcon";
import useStore from "../../hooks/global/useStore";
import {useTranslation} from "react-i18next";
import useTheme from "../../hooks/ui/useTheme";
import useAppMethods from "../../hooks/utility/useAppMethods";
import {ORDER_METHODS, RoomServiceLocationType} from "../../services/exports/Constants";
import moment from "moment";
import useOrderMethodsStatus from "../../hooks/availability/useOrderMethodsStatus";

interface Props {
  className?: string;
}

export default function OrderType(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:OrderType' });

  const { className} = props;
  const { company } = useStore()?.initialData;
  const { cached_order } = useStore()?.order;

  const theme = useTheme();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders} = useOrderMethodsStatus();
  const { formatDateTime, createScheduledOrderSlot, toggleOrderMethodModal } = useAppMethods();

  const renderTime = useMemo(() => {
    function getTime(): string {
      if (! isOrderMethodCurrentlyOpen(cached_order?.method) && ! isOrderMethodOpenForFutureOrders(cached_order?.method)) {
        return t('unavailable');
      }

      if (cached_order?.scheduled_for) {
        return createScheduledOrderSlot(cached_order?.scheduled_for);
      }

      return t('now');
    }

    return (
      <div className="flex items-center">
        <div className="h-5 w-5 mr-tiny" />
        <h6 className="mini semibold">{getTime()}</h6>
      </div>
    );
  }, [cached_order, company?.has_pre_order]);

  const renderOrderMethod = () => {
    const dateLabel = (!cached_order?.scheduled_for || moment(cached_order.scheduled_for).isSame(moment(), 'day'))
      ? t('today')
      : formatDateTime(cached_order.scheduled_for, { format: 'dddd, DD MMM' });

    if (cached_order?.method === ORDER_METHODS.ROOM_SERVICE) {
      return cached_order?.room_service_location?.type === RoomServiceLocationType.RoomService
        ? t('room_service', { date: dateLabel })
        : t('table_service', { date: dateLabel });
    }

    return cached_order?.method === ORDER_METHODS.DELIVERY
      ? t('delivery', { date: dateLabel })
      : t('pickup', { date: dateLabel });
  }

  return (
    <div
      className={classnames('relative flex justify-between items-center p-xsmall rounded-lg bg-brand-inkGrey-grey_6 cursor-pointer z-10', className)}
      onClick={() => toggleOrderMethodModal({ openModal: true })}
    >
      <div>
        <div className="flex items-center">
          <LocationIcon
            color={theme.color.text.default}
            className="h-5 w-5 mr-tiny"
          />
          <h6>{renderOrderMethod()}</h6>
        </div>
        {renderTime}
      </div>
      <button
        className="bg-background-inkWhite-white_1 h-fit p-tiny rounded text-mini font-semibold"
      >{t('buttons.edit')}</button>
    </div>
  );
}